import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowRight, ArrowLeft, Phone, ExternalLink, CheckCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fade = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6 } } };

export default function AIToolPage() {
  const { toolSlug } = useParams();
  const [tool, setTool] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/ai-tools/${toolSlug}`)
      .then(r => { setTool(r.data); setLoading(false); })
      .catch(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [toolSlug]);

  if (loading) return <div className="min-h-screen bg-black flex items-center justify-center"><div className="w-6 h-6 border-2 border-white/30 border-t-white animate-spin" /></div>;
  if (!tool) return (
    <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center gap-6 px-6">
      <p className="text-xl">Tool not found</p>
      <Link to="/ai-services" className="text-zinc-400 hover:text-white text-xs tracking-wider">Back to AI Services</Link>
    </div>
  );

  return (
    <div className="min-h-screen bg-black text-white">
      {/* HEADER */}
      <section className="pt-32 pb-20 px-6 md:px-16 lg:px-28">
        <Link to="/ai-services" className="inline-flex items-center gap-2 text-[10px] text-zinc-500 hover:text-white transition-colors mb-10 tracking-[0.2em]" data-testid="tool-back-link">
          <ArrowLeft className="w-3 h-3" /> AI Services
        </Link>
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <p className="text-zinc-500 text-[10px] tracking-[0.3em] mb-4">{tool.category}</p>
          <h1 data-testid="tool-title" className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.1]">
            {tool.name}
          </h1>
          <p className="text-zinc-500 text-xs tracking-wider mt-2">{tool.provider}</p>
          <p className="text-zinc-400 text-sm mt-8 max-w-2xl leading-relaxed tracking-wider">{tool.tagline}</p>
        </motion.div>
      </section>

      {/* WHAT IT DOES */}
      <motion.section variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }}
        className="py-24 px-6 md:px-16 lg:px-28 border-t border-white/10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <p className="text-zinc-500 text-xs tracking-[0.3em] mb-6">What it does</p>
            <p className="text-zinc-300 text-sm leading-relaxed tracking-wider">{tool.description}</p>
          </div>
          <div>
            <p className="text-zinc-500 text-xs tracking-[0.3em] mb-6">Pricing</p>
            <p className="text-zinc-300 text-sm leading-relaxed tracking-wider">{tool.pricing}</p>
          </div>
        </div>
      </motion.section>

      {/* HOW PROSPERA USES IT */}
      <motion.section variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }}
        className="py-24 px-6 md:px-16 lg:px-28 border-t border-white/10">
        <p className="text-zinc-500 text-xs tracking-[0.3em] mb-6">How Prospera uses {tool.name}</p>
        <h2 className="text-2xl md:text-3xl tracking-tight max-w-2xl">{tool.how_we_use_it}</h2>
      </motion.section>

      {/* USE CASES */}
      <motion.section variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }}
        data-testid="tool-use-cases" className="py-24 px-6 md:px-16 lg:px-28 border-t border-white/10">
        <p className="text-zinc-500 text-xs tracking-[0.3em] mb-6">Marketing use cases</p>
        <h2 className="text-2xl md:text-3xl tracking-tight">What you can do with {tool.name}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px mt-12 bg-white/10">
          {tool.use_cases.map((uc, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.08 }} className="bg-black p-8 flex items-start gap-3">
              <CheckCircle className="w-4 h-4 text-zinc-500 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-zinc-300 tracking-wider leading-relaxed">{uc}</p>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* WHY PROSPERA */}
      <motion.section variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }}
        className="py-24 px-6 md:px-16 lg:px-28 border-t border-white/10">
        <p className="text-zinc-500 text-xs tracking-[0.3em] mb-6">Why not do it yourself?</p>
        <h2 className="text-xl md:text-2xl tracking-tight max-w-2xl">{tool.why_prospera}</h2>
      </motion.section>

      {/* CTA */}
      <section className="py-24 px-6 md:px-16 lg:px-28 border-t border-white/10">
        <motion.div variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }}
          className="text-center max-w-xl mx-auto">
          <h2 className="text-2xl md:text-3xl tracking-tight">Want us to handle {tool.name} for you?</h2>
          <p className="text-zinc-500 text-xs mt-4 tracking-wider">We set it up, run it, optimize it. You see the results.</p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 mt-10">
            <Link to="/ai-services#ai-form" data-testid="tool-start-project"
              className="bg-white text-black px-8 py-4 text-xs tracking-[0.2em] hover:bg-zinc-200 transition-colors inline-flex items-center justify-center gap-3">
              Start a project <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="https://wa.me/919041344420?text=Hi%20Prospera%2C%20I%20need%20help%20with%20this%20AI%20tool%20for%20my%20marketing.%20Can%20we%20discuss%3F" target="_blank" rel="noopener noreferrer"
              className="border border-white/20 px-8 py-4 text-xs tracking-[0.2em] hover:border-white/50 transition-colors text-center">
              Whatsapp us
            </a>
          </div>
        </motion.div>
      </section>

      {/* RELATED TOOLS */}
      {tool.related_tools && tool.related_tools.length > 0 && (
        <section className="py-16 px-6 md:px-16 lg:px-28 border-t border-white/10">
          <p className="text-zinc-600 text-[10px] tracking-[0.3em] mb-6">Related tools</p>
          <div className="flex flex-wrap gap-3">
            {tool.related_tools.map(rt => (
              <Link key={rt.slug} to={`/ai-services/${rt.slug}`}
                className="text-[10px] tracking-wider text-zinc-400 border border-white/10 px-4 py-2 hover:border-white/40 hover:text-white transition-colors">
                {rt.name}
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* SEO */}
      <section className="py-12 px-6 md:px-16 lg:px-28 border-t border-white/10">
        <div className="flex flex-wrap gap-2">
          {tool.seo_keywords.map(kw => (
            <span key={kw} className="text-[10px] text-zinc-700 border border-white/5 px-3 py-1 tracking-wider">{kw}</span>
          ))}
        </div>
      </section>
    </div>
  );
}
